import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const ChargeReportsTable = ({ reports, isLoading, year }) => {
  const [sortField, setSortField] = useState('totalFiled');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPercentage = (rate) => {
    return (rate * 100).toFixed(1) + '%';
  };

  const stripRedundantPrefix = (description, code) => {
    if (!description || !code) return description;

    // Find the first space in the description
    const firstSpaceIndex = description.indexOf(' ');
    if (firstSpaceIndex === -1) return description;

    // Get the first word (everything up to the first space)
    const firstWord = description.substring(0, firstSpaceIndex);

    // If the first word matches the code, strip it
    if (firstWord === code) {
      return description.substring(firstSpaceIndex + 1).trim();
    }

    return description;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Filter and sort reports
  const filteredAndSortedReports = useMemo(() => {
    if (!reports) return [];

    let filtered = reports;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = reports.filter(
        (report) =>
          report.chargeCode.toLowerCase().includes(searchLower) ||
          report.chargeDescription.toLowerCase().includes(searchLower)
      );
    }

    // Sort the filtered results
    return [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'totalFiled':
          aValue = a.totalFiled;
          bValue = b.totalFiled;
          break;
        case 'convictionRate':
          aValue = a.outcomeStats.convictionRate;
          bValue = b.outcomeStats.convictionRate;
          break;
        case 'dismissalRate':
          aValue = a.outcomeStats.dismissalRate;
          bValue = b.outcomeStats.dismissalRate;
          break;
        case 'averageSentence':
          aValue = a.outcomeStats.averageSentenceMonths || 0;
          bValue = b.outcomeStats.averageSentenceMonths || 0;
          break;
        default:
          aValue = a.chargeCode;
          bValue = b.chargeCode;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [reports, sortField, sortDirection, searchTerm]);

  // Paginate the results
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedReports.slice(startIndex, endIndex);
  }, [filteredAndSortedReports, currentPage, pageSize]);

  // Calculate pagination info
  const totalPages = Math.ceil(filteredAndSortedReports.length / pageSize);
  const totalResults = filteredAndSortedReports.length;

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset to first page when page size changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortDirection === 'asc' ? (
      <svg
        className="w-4 h-4 text-blue-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 text-blue-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"
        />
      </svg>
    );
  };

  SortIcon.propTypes = {
    field: PropTypes.string.isRequired
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!reports || reports.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No charge reports found for {year}</p>
      </div>
    );
  }

  if (searchTerm && totalResults === 0) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Charge Reports for {year}
            </h3>
          </div>

          {/* Search and Controls */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by charge code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <button
              onClick={() => setSearchTerm('')}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          </div>

          <div className="text-center py-8">
            <p className="text-gray-500">
              No charges found matching &quot;{searchTerm}&quot;
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              Clear search to see all charges
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedRow(null); // Close any expanded rows when changing pages
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return (
      <div className="mt-6 space-y-4">
        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Page Size Selector */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-sm text-gray-700">Show:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <span className="text-sm text-gray-700">per page</span>
          </div>

          {/* Results Info */}
          <div className="text-center text-sm text-gray-700 mb-4">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, totalResults)} of{' '}
            {formatNumber(totalResults)} results
          </div>

          {/* Mobile Pagination - Simplified */}
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>

            {/* Show only current page and a few surrounding pages on mobile */}
            <div className="flex items-center gap-1">
              {currentPage > 2 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {currentPage > 1 && (
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {currentPage - 1}
                </button>
              )}

              <button className="px-3 py-2 text-sm border border-blue-600 bg-blue-600 text-white rounded-md">
                {currentPage}
              </button>

              {currentPage < totalPages && (
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {currentPage + 1}
                </button>
              )}

              {currentPage < totalPages - 1 && (
                <>
                  {currentPage < totalPages - 2 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">Show:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
            <div className="text-sm text-gray-700">
              Showing {(currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, totalResults)} of{' '}
              {formatNumber(totalResults)} results
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>

            {pages.map((pageNum, idx) =>
              pageNum === '...' ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-3 py-2 text-gray-500"
                >
                  ...
                </span>
              ) : (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm border rounded-md ${
                    pageNum === currentPage
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              )
            )}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Charge Reports for {year}
          </h3>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              {formatNumber(totalResults)} charges found
            </span>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by charge code or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
          )}
        </div>

        {/* Mobile Layout - Card-based */}
        <div className="lg:hidden space-y-4">
          {paginatedReports.map((report, index) => (
            <div
              key={report.chargeCode}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              {/* Header Row */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {report.chargeCode}
                  </div>
                  <div className="text-sm text-gray-600 line-clamp-2">
                    {stripRedundantPrefix(
                      report.chargeDescription,
                      report.chargeCode
                    )}
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  <button
                    onClick={() =>
                      setExpandedRow(
                        expandedRow === report.chargeCode
                          ? null
                          : report.chargeCode
                      )
                    }
                    className="text-blue-600 hover:text-blue-500 font-medium text-xs px-2 py-1 rounded border border-blue-200 hover:bg-blue-50"
                  >
                    {expandedRow === report.chargeCode ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatNumber(report.totalFiled)}
                  </div>
                  <div className="text-xs text-gray-500">Count</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900">
                    {report.outcomeStats.averageSentenceMonths
                      ? `${report.outcomeStats.averageSentenceMonths.toFixed(0)}m`
                      : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500">Avg Sentence</div>
                </div>
              </div>

              {/* Rate Badges */}
              <div className="flex gap-2 mb-3">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    report.outcomeStats.convictionRate > 0.7
                      ? 'bg-green-100 text-green-800'
                      : report.outcomeStats.convictionRate > 0.4
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                >
                  {formatPercentage(report.outcomeStats.convictionRate)}{' '}
                  convicted
                </span>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    report.outcomeStats.dismissalRate > 0.5
                      ? 'bg-red-100 text-red-800'
                      : report.outcomeStats.dismissalRate > 0.2
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                  }`}
                >
                  {formatPercentage(report.outcomeStats.dismissalRate)}{' '}
                  dismissed
                </span>
              </div>

              {/* Expanded Details */}
              {expandedRow === report.chargeCode && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="space-y-4">
                    {/* Courthouse Breakdown */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        By Courthouse
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(report.courthouseBreakdown)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 5)
                          .map(([courthouse, count]) => (
                            <div
                              key={courthouse}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate">{courthouse}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Judge Breakdown */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        By Judge
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(report.judgeBreakdown)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 5)
                          .map(([judge, count]) => (
                            <div
                              key={judge}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate">{judge}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Prosecutor Breakdown */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        By Prosecutor
                      </h4>
                      <div className="space-y-1">
                        {Object.entries(report.prosecutorBreakdown)
                          .sort(([, a], [, b]) => b - a)
                          .slice(0, 5)
                          .map(([prosecutor, count]) => (
                            <div
                              key={prosecutor}
                              className="flex justify-between text-xs"
                            >
                              <span className="truncate">{prosecutor}</span>
                              <span className="font-medium">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Sentencing Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">
                        Sentencing Distribution
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(
                          report.outcomeStats.sentencingDistribution
                        )
                          .sort(([, a], [, b]) => b - a)
                          .map(([sentence, count]) => (
                            <div key={sentence} className="text-xs">
                              <span className="text-gray-600">{sentence}:</span>
                              <span className="ml-1 font-medium">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Layout - Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="w-16 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('chargeCode')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Code</span>
                    <SortIcon field="chargeCode" />
                  </div>
                </th>
                <th className="w-48 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th
                  className="w-12 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalFiled')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Count</span>
                    <SortIcon field="totalFiled" />
                  </div>
                </th>
                <th
                  className="w-16 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('convictionRate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Convicted</span>
                    <SortIcon field="convictionRate" />
                  </div>
                </th>
                <th
                  className="w-16 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('dismissalRate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Dismissed</span>
                    <SortIcon field="dismissalRate" />
                  </div>
                </th>
                <th
                  className="w-16 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('averageSentence')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Avg Sentence</span>
                    <SortIcon field="averageSentence" />
                  </div>
                </th>
                <th className="w-14 px-1 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedReports.map((report, index) => (
                <React.Fragment key={report.chargeCode}>
                  <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-1 py-2 whitespace-nowrap text-sm font-medium text-gray-900 w-16">
                      {report.chargeCode}
                    </td>
                    <td className="px-1 py-2 text-sm text-gray-900 w-48 max-w-48">
                      <div
                        className="truncate max-w-full"
                        title={report.chargeDescription}
                        style={{ maxWidth: '192px' }}
                      >
                        {stripRedundantPrefix(
                          report.chargeDescription,
                          report.chargeCode
                        )}
                      </div>
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                      {formatNumber(report.totalFiled)}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span
                        className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${
                          report.outcomeStats.convictionRate > 0.7
                            ? 'bg-green-100 text-green-800'
                            : report.outcomeStats.convictionRate > 0.4
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {formatPercentage(report.outcomeStats.convictionRate)}
                      </span>
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                      <span
                        className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${
                          report.outcomeStats.dismissalRate > 0.5
                            ? 'bg-red-100 text-red-800'
                            : report.outcomeStats.dismissalRate > 0.2
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {formatPercentage(report.outcomeStats.dismissalRate)}
                      </span>
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                      {report.outcomeStats.averageSentenceMonths
                        ? `${report.outcomeStats.averageSentenceMonths.toFixed(0)}m`
                        : 'N/A'}
                    </td>
                    <td className="px-1 py-2 whitespace-nowrap text-sm text-gray-900 text-center">
                      <button
                        onClick={() =>
                          setExpandedRow(
                            expandedRow === report.chargeCode
                              ? null
                              : report.chargeCode
                          )
                        }
                        className="text-blue-600 hover:text-blue-500 font-medium text-xs"
                      >
                        {expandedRow === report.chargeCode ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === report.chargeCode && (
                    <tr className="bg-blue-50">
                      <td colSpan="7" className="px-1 py-2">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {/* Courthouse Breakdown */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                              By Courthouse
                            </h4>
                            <div className="space-y-1">
                              {Object.entries(report.courthouseBreakdown)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5)
                                .map(([courthouse, count]) => (
                                  <div
                                    key={courthouse}
                                    className="flex justify-between text-xs"
                                  >
                                    <span className="truncate">
                                      {courthouse}
                                    </span>
                                    <span className="font-medium">{count}</span>
                                  </div>
                                ))}
                            </div>
                          </div>

                          {/* Judge Breakdown */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                              By Judge
                            </h4>
                            <div className="space-y-1">
                              {Object.entries(report.judgeBreakdown)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5)
                                .map(([judge, count]) => (
                                  <div
                                    key={judge}
                                    className="flex justify-between text-xs"
                                  >
                                    <span className="truncate">{judge}</span>
                                    <span className="font-medium">{count}</span>
                                  </div>
                                ))}
                            </div>
                          </div>

                          {/* Prosecutor Breakdown */}
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 mb-2">
                              By Prosecutor
                            </h4>
                            <div className="space-y-1">
                              {Object.entries(report.prosecutorBreakdown)
                                .sort(([, a], [, b]) => b - a)
                                .slice(0, 5)
                                .map(([prosecutor, count]) => (
                                  <div
                                    key={prosecutor}
                                    className="flex justify-between text-xs"
                                  >
                                    <span className="truncate">
                                      {prosecutor}
                                    </span>
                                    <span className="font-medium">{count}</span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>

                        {/* Sentencing Distribution */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Sentencing Distribution
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {Object.entries(
                              report.outcomeStats.sentencingDistribution
                            )
                              .sort(([, a], [, b]) => b - a)
                              .map(([sentence, count]) => (
                                <div key={sentence} className="text-xs">
                                  <span className="text-gray-600">
                                    {sentence}:
                                  </span>
                                  <span className="ml-1 font-medium">
                                    {count}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>
    </div>
  );
};

ChargeReportsTable.propTypes = {
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      chargeCode: PropTypes.string,
      chargeDescription: PropTypes.string,
      totalFiled: PropTypes.number,
      year: PropTypes.string,
      courthouseBreakdown: PropTypes.object,
      judgeBreakdown: PropTypes.object,
      prosecutorBreakdown: PropTypes.object,
      outcomeStats: PropTypes.shape({
        totalCases: PropTypes.number,
        convictedCount: PropTypes.number,
        dismissedCount: PropTypes.number,
        diversionCount: PropTypes.number,
        convictionRate: PropTypes.number,
        dismissalRate: PropTypes.number,
        diversionRate: PropTypes.number,
        averageSentenceMonths: PropTypes.number,
        averageFines: PropTypes.number
      })
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  year: PropTypes.string.isRequired
};

export default ChargeReportsTable;
